import './List.css'

import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ManagerRoles, Roles, User , isAdminRole } from '@/services/repository/users'
import { SearchInput } from '@/components/search/SearchInput'
import { useAppContext } from '@/context/app'
import { Spinner } from '@/components/spinner'
import { SimpleMachine } from '@/pages/communal/my/MyMachines'
import { useModal } from '@/components/modal/Modal'
import { Menu, MenuPopupBody } from '@/popups/menupopup/menu'
import { MachinePopupQRCodeBody } from '@/popups/qrcode/qrcode'

function groupMachinesByManufacturerAndModel(machines: SimpleMachine[]) {
  const result: Record<string, Record<string, SimpleMachine[]>> = {};
  machines.forEach(machine => {
    const manufacturer = machine.manufacturer || ''
    const model = machine.model || ''
    if (!result[manufacturer]) result[manufacturer] = {};
    if (!result[manufacturer][model]) result[manufacturer][model] = [];
    result[manufacturer][model].push(machine);
  });
  return result;
}

const emptyLastSort = (a: string, b: string) => {
  if (!a && b) return 1;     // a is empty, b is not: a comes last
  if (a && !b) return -1;    // b is empty, a is not: b comes last
  return a.localeCompare(b); // both non-empty or both empty
};

export const MachineMenuPopup = ({ onClose, row }: {
  onClose: () => void
  row: SimpleMachine
}) => {
  const {translates, services, session} = useAppContext()
  const navigate = useNavigate()
  const { openModal } = useModal()
  const title = `${row.manufacturer || ''} ${row.model || ''} ${row.internalNumber ? row.internalNumber : row.serialNumber || ''}`
  const menu: Menu = {
    title,
    rows: [
      {
        icon: 'svgfont-link',
        title: 'elements.table.machines.menu.dashboard.open_machine_page',
        action: () => navigate(`/communal/machine/${row.id}`)
      },
      {
        icon: 'svgfont-link',
        title: 'elements.table.machines.menu.dashboard.open_machine_statistic_page',
        action: () => navigate(`/communal/statistics/machines?machine=${row.id}`)
      },
      {
        icon: 'svgfont-qrcode',
        title: 'elements.table.machines.menu.edit.show_qr_code',
        action: () => openModal((props) => MachinePopupQRCodeBody({ ...props, machineId: row.id, services, session, translates: translates, title }))
      },
    ]
  }

  return <MenuPopupBody menu={menu} onClose={onClose} />
}

export const DashboardMachinesTab = ({ }: {}) => {
  const { session, translates, services } = useAppContext()
  const { openModal } = useModal()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [machines, setMachines] = useState<SimpleMachine[]>()

  useEffect(() => {
    if (!session) {
      return
    }
    const fetchUrl = isAdminRole(session.user.role) ?
      `/api/superadmin/machines/search?pageNumber=1&pageSize=2000&search=`
      : `/api/communal/my/machines/search?pageNumber=1&pageSize=2000&search=`
    services.session.fetchWithToken(fetchUrl).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        console.error(erros)
        return Promise.reject<any>()
      }
      const resp = await r.json()
      setMachines(resp.data)
    })
  }, [session]);

  const filtred = useMemo(() => {
    if (!machines) {
      return undefined
    }
    if (!debouncedSearch || debouncedSearch === '') {
      return machines
    }
    return machines?.filter((m) => {
      return (m.manufacturer && m.manufacturer.indexOf(debouncedSearch) >= 0) ||
        (m.model && m.model.indexOf(debouncedSearch) >= 0) ||
        (m.internalNumber && m.internalNumber.indexOf(debouncedSearch) >= 0)
    })
  }, [machines, debouncedSearch])

  const groupedMachines = useMemo(() => filtred && groupMachinesByManufacturerAndModel(filtred), [filtred]);

  if (!translates || !machines) {
    return <Spinner />
  }

  return <div className='dashboard-machines-tab'>
    <div className='dashboard-machines-tab-content'>
      <SearchInput translates={translates} value={search} onChange={setSearch} onDebouncedChange={setDebouncedSearch} />

      <div className='machines-list'>
        {groupedMachines &&
          Object.entries(groupedMachines)
            .sort(([a], [b]) => emptyLastSort(a, b)) // Empty keys last
            .map(([manufacturer, models]) => (
              <div key={manufacturer} className="machine-group">
                {manufacturer ? <h3>{manufacturer}</h3> : null}
                {Object.entries(models)
                  .sort(([a], [b]) => emptyLastSort(a, b)) // Sort models
                  .map(([model, machineList]) => (
                    <div key={model} className="machine-model-group">
                      {model ? <h4>{model}</h4> : null}
                      {machineList
                        .slice() // Avoid mutating original array
                        .sort((a, b) =>
                          (a.internalNumber || a.id).localeCompare(b.internalNumber || b.id)
                        ) // Sort machineList
                        .map(machine => (
                          <div
                            key={machine.id}
                            className="machine" >
                            <div className='text'
                              onClick={() => {
                                if (ManagerRoles.includes(session?.user.role as Roles)) {
                                  navigate(`/communal/statistics/machines?machine=${machine.id}`);
                                  return;
                                }
                                navigate(`/communal/machine/${machine.id}`);
                              }}>
                              {machine.internalNumber || machine.id}
                            </div>
                            <div className='menu-btn' onClick={() => openModal((props) => MachineMenuPopup({ ...props, row: machine }))}>
                              <img src='/public/img/dashboard/menu.svg' />
                            </div>
                          </div>
                        ))}
                    </div>
                  ))}
              </div>
            ))}

        {machines?.length === 0 ?
          <div className="empty-search-table-message">
            {translates.for("elements.search.empty_table")}
          </div> : null}
      </div>
    </div>
  </div>
}

